/**
 * SecureEmbed Search Page
 *
 * Tier 1 - Semantic search across all data sources
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search as SearchIcon, Filter, Loader2, AlertCircle, MapPin,
  Calendar, DollarSign, Sparkles, AlertTriangle, RefreshCw,
  Building2, Globe, ExternalLink, Link2
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { searchApi, partnerApi, PartnerOrg } from '../services/api';
import { CHART_COLORS, RECHARTS_THEME } from '../constants/colors';
import DataSourceBadge from '../components/DataSourceBadge';
import ChartCard from '../components/ChartCard';
import TierGate from '../components/TierGate';
import RelatedPagesCard from '../components/RelatedPagesCard';
import InfoTooltip from '../components/InfoTooltip';
import ConnectionsExplorer from '../components/ConnectionsExplorer';
import PeriodSelector from '../components/PeriodSelector';

interface SearchResult {
  id: string;
  score: number;
  content: string;
  source: string;
  region?: string;
  timestamp?: string;
  metadata?: Record<string, any>;
}

const SAMPLE_QUERIES = [
  'renewable energy developer Queensland',
  'battery storage operator Australia',
  'wind farm developer NSW',
  'BESS competitors in NEM',
  'AEMO registered generator above 100MW',
  'solar farm EPC contractor',
  'electricity demand forecast NSW',
  'grid stability peak hours',
  'price volatility factors',
  'How does wind generation correlate with price drops?',
  'Which regions had the highest price volatility this year?',
  'Show me FCAS market trends for South Australia',
];

const DATASOURCE_OPTIONS = [
  { key: '', label: 'All Sources' },
  { key: 'aemo', label: 'AEMO Energy' },
  { key: 'bom', label: 'BOM Weather' },
  { key: 'aer', label: 'AER Regulatory' },
  { key: 'aemo-duid', label: 'AEMO DUID' },
  { key: 'aemo-fcas', label: 'AEMO FCAS' },
  { key: 'regulatory', label: 'Regulatory' },
  { key: 'energy-organisations', label: 'Energy Orgs' },
  { key: 'grid-connection-queue', label: 'Grid Queue' },
  { key: 'rez-capacity', label: 'REZ Capacity' },
  { key: 'ppa-intelligence', label: 'PPA Intel' },
  { key: 'network-constraints', label: 'Network' },
  { key: 'planning-approvals', label: 'Planning' },
  { key: 'construction-milestones', label: 'Construction' },
  { key: 'community-sentiment', label: 'Sentiment' },
  { key: 'partner-discovery', label: 'Partner Discovery' },
];

// I2: NEM region filter chips for Energy Orgs datasource
const NEM_REGION_FILTERS = [
  { key: 'NSW1', label: 'NSW' },
  { key: 'VIC1', label: 'VIC' },
  { key: 'QLD1', label: 'QLD' },
  { key: 'SA1', label: 'SA' },
  { key: 'TAS1', label: 'TAS' },
];

// I4: BESS-specific sample queries shown when Energy Orgs chip is selected
const BESS_SAMPLE_QUERIES = [
  'battery energy storage projects in NEM',
  'BESS revenue stacking FCAS arbitrage',
  'grid-scale battery installations above 100MW',
  'BESS competitors in National Electricity Market',
  'utility-scale battery storage developer Australia',
  'battery dispatch optimisation NEM',
];

const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queryTimeMs, setQueryTimeMs] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState<'search' | 'connections' | 'anomalies'>('search');

  // Read URL params on mount to support deep linking (e.g. from RelatedConnections)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const qParam = searchParams.get('q');
    if (tabParam === 'connections' || tabParam === 'anomalies' || tabParam === 'search') {
      setActiveTab(tabParam);
    }
    if (qParam) {
      setQuery(qParam);
    }
    // Clear URL params after reading so they don't persist on subsequent navigation
    if (tabParam || qParam) {
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filters
  const [datasourceFilter, setDatasourceFilter] = useState('');
  const [partnerResults, setPartnerResults] = useState<PartnerOrg[]>([]);
  const [regionFilter, setRegionFilter] = useState('');
  const [nemRegionFilter, setNemRegionFilter] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  // Period selector
  const [timeRange, setTimeRange] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Anomalies
  const [anomalies, setAnomalies] = useState<any[]>([]);
  const [anomalyLoading, setAnomalyLoading] = useState(false);

  // Facets
  const [facetData, setFacetData] = useState<any[]>([]);
  const [sourceFacetData, setSourceFacetData] = useState<any[]>([]);

  // Source Type filter
  const [sourceTypeFilter, setSourceTypeFilter] = useState('');

  // DUID filter
  const [duidFilter, setDuidFilter] = useState('');

  const handleSearch = useCallback(async (searchQuery?: string) => {
    const q = searchQuery || query;
    if (!q.trim()) return;

    setIsSearching(true);
    setError(null);
    setResults([]);
    setPartnerResults([]);
    setFacetData([]);
    setSourceFacetData([]);

    try {
      if (datasourceFilter === 'partner-discovery') {
        const response = await partnerApi.search(q, { limit: 20 });
        if (response.success && response.data) {
          setPartnerResults(response.data.results || []);
          setQueryTimeMs(response.data.query_time_ms || null);
        }
      } else {
        const response = await searchApi.similar(q, {
          datasource: datasourceFilter || undefined,
          region: (datasourceFilter === 'energy-organisations' ? nemRegionFilter : regionFilter) || undefined,
          date_start: dateStart || undefined,
          date_end: dateEnd || undefined,
          limit: 20,
        });

        if (response.success && response.data) {
          let filtered = response.data.results || [];
          if (sourceTypeFilter) {
            filtered = filtered.filter(r => r.source === sourceTypeFilter);
          }
          setResults(filtered);
          setQueryTimeMs(response.data.query_time_ms || null);
        }

        // Fetch region and source facets in parallel
        try {
          const [regionFacets, sourceFacets] = await Promise.allSettled([
            searchApi.faceted({ field: 'region', datasource: datasourceFilter || undefined }),
            searchApi.faceted({ field: 'source', datasource: datasourceFilter || undefined }),
          ]);
          if (regionFacets.status === 'fulfilled' && regionFacets.value?.success && regionFacets.value?.data?.facets) {
            setFacetData(regionFacets.value.data.facets.map((f: any) => ({
              name: f.label,
              count: f.count,
            })));
          }
          if (sourceFacets.status === 'fulfilled' && sourceFacets.value?.success && sourceFacets.value?.data?.facets) {
            setSourceFacetData(sourceFacets.value.data.facets.map((f: any) => ({
              name: f.label.replace(/_/g, ' '),
              count: f.count,
            })));
          }
        } catch {
          // Facets are optional
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  }, [query, datasourceFilter, regionFilter, nemRegionFilter, dateStart, dateEnd, duidFilter, sourceTypeFilter]);

  const loadAnomalies = useCallback(async () => {
    setAnomalyLoading(true);
    try {
      const response = await searchApi.anomalies({
        datasource: datasourceFilter || undefined,
        limit: 10,
      });
      if (response?.success && response?.data?.anomalies) {
        setAnomalies(response.data.anomalies);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load anomalies');
    } finally {
      setAnomalyLoading(false);
    }
  }, [datasourceFilter]);

  const getScoreColor = (score: number): string => {
    if (score >= 0.9) return 'bg-green-100 text-green-700';
    if (score >= 0.7) return 'bg-blue-100 text-blue-700';
    if (score >= 0.5) return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-700';
  };

  const handleSearchFromHere = useCallback((term: string) => {
    setQuery(term);
    setActiveTab('search');
    handleSearch(term);
  }, [handleSearch]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Semantic Search</h1>
        <p className="text-gray-500 mt-1">Search across all data sources using AI embeddings</p>
      </div>

      {/* Dataset Selector Chips */}
      <div className="flex flex-wrap gap-2">
        {DATASOURCE_OPTIONS.map(ds => (
          <button
            key={ds.key}
            onClick={() => setDatasourceFilter(ds.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              datasourceFilter === ds.key
                ? 'bg-[#2B064F] text-white shadow-sm'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {ds.label}
          </button>
        ))}
      </div>

      {/* I2: NEM Region Filter — shown only when Energy Orgs selected */}
      {datasourceFilter === 'energy-organisations' && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs text-gray-500 mr-1">NEM Region:</span>
          <button
            onClick={() => setNemRegionFilter('')}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              !nemRegionFilter
                ? 'bg-violet-600 text-white shadow-sm'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            All Regions
          </button>
          {NEM_REGION_FILTERS.map(nr => (
            <button
              key={nr.key}
              onClick={() => setNemRegionFilter(nemRegionFilter === nr.key ? '' : nr.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                nemRegionFilter === nr.key
                  ? 'bg-violet-600 text-white shadow-sm'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {nr.label}
            </button>
          ))}
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Ask a question or enter a semantic search query..."
              className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2B064F]/50 focus:border-[#2B064F]"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            title="Filters"
            aria-label="Toggle search filters"
            className={`px-4 py-3.5 border rounded-2xl transition-colors ${showFilters ? 'bg-[#2B064F] text-white border-[#2B064F]' : 'border-gray-200 hover:bg-gray-50'}`}
          >
            <Filter className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleSearch()}
            disabled={isSearching || !query.trim()}
            className="px-8 py-3.5 bg-gradient-to-r from-[#2B064F] to-indigo-700 text-white rounded-2xl font-medium hover:from-[#3D0A6B] hover:to-indigo-800 transition-all disabled:opacity-50 shadow-sm"
          >
            {isSearching ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Search'}
          </button>
        </div>

        {/* Quick queries — I4: show BESS queries when Energy Orgs selected */}
        <div className="flex flex-wrap gap-2 mt-3">
          {(datasourceFilter === 'energy-organisations' ? BESS_SAMPLE_QUERIES : SAMPLE_QUERIES).map(sq => (
            <button
              key={sq}
              onClick={() => { setQuery(sq); handleSearch(sq); }}
              className="px-3 py-1.5 text-xs rounded-full bg-purple-50 text-purple-600 border border-purple-200 hover:bg-purple-100 transition-colors"
            >
              {sq}
            </button>
          ))}
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 pt-4 border-t border-gray-100">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Source Type</label>
              <select
                value={sourceTypeFilter}
                onChange={(e) => setSourceTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2B064F]/50"
              >
                <option value="">All Types</option>
                <option value="aemo_ingestion">AEMO Market Data</option>
                <option value="bom_ingestion">BOM Weather</option>
                <option value="aer_ingestion">AER Regulatory</option>
                <option value="scholar_ingestion">Scholarly</option>
                <option value="aemo_duid_ingestion">AEMO DUID</option>
                <option value="aemo_fcas_ingestion">AEMO FCAS</option>
                <option value="regulatory_ingestion">Regulatory</option>
                <option value="partner_ingestion">Partner</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">DUID</label>
              <input
                type="text"
                value={duidFilter}
                onChange={(e) => setDuidFilter(e.target.value)}
                placeholder="e.g. BAYSW1"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2B064F]/50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Region</label>
              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2B064F]/50"
              >
                <option value="">All Regions</option>
                <option value="NSW1">NSW</option>
                <option value="VIC1">VIC</option>
                <option value="QLD1">QLD</option>
                <option value="SA1">SA</option>
                <option value="TAS1">TAS</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date From</label>
              <input
                type="date"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2B064F]/50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date To</label>
              <input
                type="date"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2B064F]/50"
              />
            </div>
          </div>
        )}
      </div>

      {/* Period Selector */}
      <PeriodSelector
        value={timeRange}
        onChange={setTimeRange}
        customStart={customStart}
        customEnd={customEnd}
        onCustomStartChange={setCustomStart}
        onCustomEndChange={setCustomEnd}
        hint="Applied to search & anomaly detection"
      />

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('search')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'search' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
        >
          Search Results
        </button>
        <button
          onClick={() => setActiveTab('connections')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === 'connections' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
        >
          <Link2 className="w-3.5 h-3.5" />
          Connections
        </button>
        <button
          onClick={() => { setActiveTab('anomalies'); if (anomalies.length === 0) loadAnomalies(); }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'anomalies' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
        >
          Anomaly Detection
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {activeTab === 'search' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Results */}
          <div className="lg:col-span-2 space-y-3">
            {queryTimeMs !== null && results.length > 0 && (
              <p className="text-sm text-gray-500">
                {results.length} results in {queryTimeMs}ms
              </p>
            )}

            {isSearching && (
              <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
                <Loader2 className="w-8 h-8 text-[#2B064F] animate-spin mx-auto mb-4" />
                <p className="text-gray-500">Searching embeddings...</p>
              </div>
            )}

            {!isSearching && results.length === 0 && partnerResults.length === 0 && query && (
              <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
                <SearchIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No results found</p>
              </div>
            )}

            {!isSearching && results.length === 0 && partnerResults.length === 0 && !query && (
              <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
                <Sparkles className="w-12 h-12 text-purple-300 mx-auto mb-4" />
                <p className="text-gray-700 font-medium">Enter a semantic search query</p>
                <p className="text-gray-500 text-sm mt-1">Find related records across AEMO, BOM, AER, and Partner data</p>
              </div>
            )}

            {partnerResults.map((org) => (
              <div key={org.id} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:border-gray-300 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-0.5 ${getScoreColor(org.score)}`}>
                      {(org.score * 100).toFixed(0)}% match
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-700">
                      <Building2 className="w-3 h-3" /> Partner
                    </span>
                    {org.location_country && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Globe className="w-3 h-3" /> {org.location_city ? `${org.location_city}, ` : ''}{org.location_country}
                      </span>
                    )}
                  </div>
                  {org.url && (
                    <a href={org.url} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> Visit
                    </a>
                  )}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{org.name}</h3>
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">{org.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {org.categories?.slice(0, 4).map((cat: string) => (
                    <span key={cat} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{cat}</span>
                  ))}
                  {org.facility_type && (
                    <span className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded text-xs">{org.facility_type}</span>
                  )}
                </div>
              </div>
            ))}

            {results.map((result) => (
              <div key={result.id} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:border-gray-300 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-0.5 ${getScoreColor(result.score)}`}>
                      {(result.score * 100).toFixed(0)}% match
                      <InfoTooltip tooltipId="search.result.score" />
                    </span>
                    <DataSourceBadge source={result.source} />
                    {result.region && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPin className="w-3 h-3" />
                        {result.region}
                      </span>
                    )}
                  </div>
                  {result.timestamp && (
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Calendar className="w-3 h-3" />
                      {new Date(result.timestamp).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{result.content}</p>
                {result.metadata?.price && (
                  <div className="flex items-center gap-1 mt-2 text-sm text-gray-600">
                    <DollarSign className="w-3.5 h-3.5" />
                    <span className="font-mono">{Number(result.metadata.price).toFixed(2)} $/MWh</span>
                  </div>
                )}
                {result.metadata?.rebid_reason && (
                  <div className="mt-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-1.5">
                    <span className="font-medium">Rebid:</span> {result.metadata.rebid_reason}
                  </div>
                )}
                {result.metadata?.duid && (
                  <span className="inline-block mt-2 text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-mono">
                    DUID: {result.metadata.duid}
                  </span>
                )}
                {result.metadata?.clause_number && (
                  <div className="mt-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-1.5">
                    <span className="font-medium">Clause {result.metadata.clause_number}</span>
                    {result.metadata?.section_title && ` — ${result.metadata.section_title}`}
                  </div>
                )}
                <button
                  onClick={() => { setQuery(result.content.split(':')[0]); setActiveTab('connections'); }}
                  className="flex items-center gap-1 mt-2 text-xs text-purple-600 hover:text-purple-800 transition-colors"
                >
                  <Link2 className="w-3 h-3" />
                  View connections
                </button>
              </div>
            ))}

            {results.length > 0 && (
              <TierGate requiredTier={1.5}>
                <ChartCard title="Insights Preview" subtitle="Tier 1a — Pattern analysis on search results">
                  <div className="min-h-[120px] flex flex-col items-center justify-center space-y-3 p-4 bg-purple-50 rounded-lg">
                    <Sparkles className="w-8 h-8 text-purple-400" />
                    <p className="text-base text-purple-700 font-medium text-center">
                      AI-powered insight generation available at Tier 1a
                    </p>
                    <p className="text-sm text-purple-500 text-center">
                      Pattern analysis across your search results
                    </p>
                  </div>
                </ChartCard>
              </TierGate>
            )}
          </div>

          {/* Faceted Sidebar */}
          <div className="space-y-4">
            {facetData.length > 0 && (
              <ChartCard title="Results by Region" tooltipId="search.chart.facets">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={facetData} layout="vertical">
                    <CartesianGrid {...RECHARTS_THEME.grid} />
                    <XAxis type="number" tick={RECHARTS_THEME.label} />
                    <YAxis type="category" dataKey="name" tick={RECHARTS_THEME.label} width={50} />
                    <Tooltip {...RECHARTS_THEME.tooltip} />
                    <Bar dataKey="count" fill={CHART_COLORS.primary} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {sourceFacetData.length > 0 && (
              <ChartCard title="Results by Source" tooltipId="search.chart.source-facets">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={sourceFacetData} layout="vertical">
                    <CartesianGrid {...RECHARTS_THEME.grid} />
                    <XAxis type="number" tick={RECHARTS_THEME.label} />
                    <YAxis type="category" dataKey="name" tick={RECHARTS_THEME.label} width={90} />
                    <Tooltip {...RECHARTS_THEME.tooltip} />
                    <Bar dataKey="count" fill="#7C3AED" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>
        </div>
      )}

      {activeTab === 'connections' && (
        <ConnectionsExplorer query={query} onSearchFromHere={handleSearchFromHere} />
      )}

      {activeTab === 'anomalies' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Automatically detected anomalies in embedded data</p>
            <button onClick={loadAnomalies} disabled={anomalyLoading} className="p-2 hover:bg-gray-100 rounded-lg">
              <RefreshCw className={`w-4 h-4 text-gray-600 ${anomalyLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {anomalyLoading && (
            <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
              <Loader2 className="w-8 h-8 text-[#2B064F] animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Detecting anomalies...</p>
            </div>
          )}

          {!anomalyLoading && anomalies.length === 0 && (
            <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
              <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No anomalies detected</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {anomalies.map((anomaly: any, i: number) => (
              <div key={anomaly.id || i} className={`bg-white rounded-xl p-5 border-2 shadow-sm ${
                (anomaly.score || 0) >= 0.9 ? 'border-red-300' : (anomaly.score || 0) >= 0.7 ? 'border-amber-300' : 'border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-0.5 ${
                    (anomaly.score || 0) >= 0.9 ? 'bg-red-100 text-red-700' : (anomaly.score || 0) >= 0.7 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    Score: {((anomaly.score || 0) * 100).toFixed(0)}%
                    <InfoTooltip tooltipId="search.anomaly.score" />
                  </span>
                  {anomaly.source && <DataSourceBadge source={anomaly.source} />}
                </div>
                <p className="text-sm text-gray-700">{anomaly.content || anomaly.description || 'Anomaly detected'}</p>
                {anomaly.timestamp && (
                  <p className="text-xs text-gray-400 mt-2">{new Date(anomaly.timestamp).toLocaleString()}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <RelatedPagesCard currentRoute="/search" />
    </div>
  );
};

export default SearchPage;
